import React, { useCallback, useEffect, useMemo, useState } from "react";
import ComingSoon from "../../Utility/ComingSoon/ComingSoon";
import { RiTimeLine, RiTeamLine, RiProjectorLine, RiFileList3Line } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import TimeEntryNavigation from "./TimeEntryNavigation";
import TimeSheet from "./TimeSheet";
import AllTimeEntries from "./AllTimeEntries";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTimeEntries,
  fetchTeamTimeSummary,
  fetchProjectTimeSummary,
  deleteMyTimeLog,
  updateMyTimeLog,
} from "../../Redux/Thunks/timeEntryThunk";
import {
  selectTimeEntriesData,
  selectTimeEntriesLoading,
  selectTimeEntriesError,
  selectTeamSummaryData,
  selectTeamSummaryLoading,
  selectTeamSummaryError,
  selectProjectSummaryData,
  selectProjectSummaryLoading,
  selectProjectSummaryError,
  selectDeveloperSummaryData,
  selectDeveloperSummaryLoading,
  selectDeveloperSummaryError,
  updateBillingTag,
  selectMyTimeLogSaving,
  selectMyWorkItemsPickerList,
} from "../../Redux/Slices/timeEntrySlice";
import TeamTimeSummary from "./TeamTimeSummary";
import ProjectTimeSummary from "./ProjectTimeSummary";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { fetchUsersData } from "../../Redux/Thunks/usersThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { fetchMyWorkItemsAll } from "../../Redux/Thunks/timeEntryThunk";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import { getListFromApiResponse } from "../utils/apiListMappers";
import { mapProjectsToFilterOptions } from "../Projects/projectMappers";
import {
  formatDate,
  formatDateForInput,
  formatDateRange,
  getTodayDateRange,
} from "../../utils/dateUtils";
import {
  buildMyTimeLogsFetchParams,
  buildMyTimesheetExportQueryParams,
  buildMyWorkItemsQueryParamsFromTimeEntryFilters,
  resolveWorkItemIdFromTaskSelection,
  MY_TIME_LOG_LIST_SORT,
} from "../../utils/timeEntryQueryParams";
import {
  LIST_PAGE_SIZE_OPTIONS,
  readStoredListPageSize,
  writeStoredListPageSize,
  resolveListTotalPages,
  TIME_ENTRY_TIMESHEET_LIST_PAGE_SIZE_STORAGE_KEY,
} from "../../Utility/Pagination/listPaginationConstants";
import { downloadMyTimesheetExport } from "../../utils/downloadTimeLogsExport";
import { showSuccess, showError } from "../../utils/toast";
import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import { workItemToTimesheetTaskLabel } from "./timeEntryMappers";
import {
  buildUserDropdownOptionsFromUsersData,
  legacyMemberOption,
} from "../../utils/userFilterOptions";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import RequirePermission from "../../Utility/RequirePermission/RequirePermission";
import { resolveTimesheetSummaryFromMetaOrRows } from "./timeEntryDisplayUtils";

const INITIAL_TIME_FILTERS = {
  client: [],
  project: [],
  task: [],
  team: [],
  user: [],
  view: "Week",
};

/** Week (Sun–Sat) containing the given date — team / project summary bounds */
const getWeekRange = (date) => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = start.getDate() - day;
  start.setDate(diff);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const TimeEntry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { can } = usePermissions();

  const canViewTimesheet = can(PERMISSION_KEYS.TIME_ENTRY_TIMESHEET_VIEW);
  const canViewAllTimeEntries = can(PERMISSION_KEYS.ALL_TIME_ENTRY_TIMESHEET_VIEW);
  const canViewTeamSummary = can(PERMISSION_KEYS.TIME_ENTRY_TEAM_SUMMARY_VIEW);
  const canViewProjectSummary = can(PERMISSION_KEYS.TIME_ENTRY_PROJECT_SUMMARY_VIEW);

  const timeEntryNavItems = useMemo(
    () =>
      [
        { id: "timesheet", icon: RiTimeLine, label: "My Timesheet", visible: canViewTimesheet },
        {
          id: "all",
          icon: RiFileList3Line,
          label: "All Time Entries",
          visible: canViewAllTimeEntries,
        },
        {
          id: "team",
          icon: RiTeamLine,
          label: "Team Time Summary",
          visible: canViewTeamSummary,
        },
        {
          id: "project",
          icon: RiProjectorLine,
          label: "Project Time Summary",
          visible: canViewProjectSummary,
        },
      ].filter((item) => item.visible),
    [canViewTimesheet, canViewAllTimeEntries, canViewTeamSummary, canViewProjectSummary]
  );

  const firstAllowedTabId = timeEntryNavItems[0]?.id ?? "timesheet";
  const timeEntriesData = useSelector(selectTimeEntriesData);

  const loading = useSelector(selectTimeEntriesLoading);
  const error = useSelector(selectTimeEntriesError);
  const myTimeLogSaving = useSelector(selectMyTimeLogSaving);
  const teamSummaryData = useSelector(selectTeamSummaryData);
  const teamSummaryLoading = useSelector(selectTeamSummaryLoading);
  const teamSummaryError = useSelector(selectTeamSummaryError);
  const projectSummaryData = useSelector(selectProjectSummaryData);
  const projectSummaryLoading = useSelector(selectProjectSummaryLoading);
  const projectSummaryError = useSelector(selectProjectSummaryError);
  const developerSummaryData = useSelector(selectDeveloperSummaryData);
  const developerSummaryLoading = useSelector(selectDeveloperSummaryLoading);
  const developerSummaryError = useSelector(selectDeveloperSummaryError);
  const teamsData = useSelector(selectTeamsData);
  const usersData = useSelector(selectUsersData);
  const clientsData = useSelector(selectClientsData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const myWorkItemsList = useSelector(selectMyWorkItemsPickerList);

  const getActiveTabFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("tab") || "timesheet";
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());
  const [currentDate, setCurrentDate] = useState(new Date());
  /** Applied week range (from Filter button); drives table date window */
  const [timesheetDateRange, setTimesheetDateRange] = useState(null);
  /** Draft week range in filter panel only */
  const [timesheetDateRangeDraft, setTimesheetDateRangeDraft] = useState(null);
  /** Per-tab filter panel open state — survives refetches that would otherwise remount GlobalFilter */
  const [timeEntryFilterExpanded, setTimeEntryFilterExpanded] = useState({
    timesheet: false,
    all: false,
    team: false,
    project: false,
  });

  // Draft values in filter panel; applied to table only when user clicks Filter
  const [filterDraft, setFilterDraft] = useState(() => ({ ...INITIAL_TIME_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...INITIAL_TIME_FILTERS }));
  /** workItemId for GET /time-logs/my — resolved once on Filter from current rows + single task selection */
  const [appliedWorkItemId, setAppliedWorkItemId] = useState(null);
  const [timesheetPage, setTimesheetPage] = useState(1);
  const [timesheetPageSize, setTimesheetPageSize] = useState(() =>
    readStoredListPageSize(TIME_ENTRY_TIMESHEET_LIST_PAGE_SIZE_STORAGE_KEY)
  );

  const myWorkItemsAllQueryParams = useMemo(
    () =>
      buildMyWorkItemsQueryParamsFromTimeEntryFilters({
        appliedFilters,
        projectsData,
        clientsData,
        teamsData,
      }),
    [appliedFilters, projectsData, clientsData, teamsData]
  );

  const timesheetFetchParams = useMemo(
    () =>
      buildMyTimeLogsFetchParams({
        currentDate,
        timesheetDateRange,
        appliedFilters,
        projectsData,
        clientsData,
        teamsData,
        appliedWorkItemId,
        page: timesheetPage,
        limit: timesheetPageSize,
        sortBy: MY_TIME_LOG_LIST_SORT.sortBy,
        sortOrder: MY_TIME_LOG_LIST_SORT.sortOrder,
        includeTeamUserFilters: false,
      }),
    [
      currentDate,
      timesheetDateRange,
      appliedFilters,
      projectsData,
      clientsData,
      teamsData,
      appliedWorkItemId,
      timesheetPage,
      timesheetPageSize,
    ]
  );

  const refetchTimesheetEntries = useCallback(() => {
    dispatch(fetchTimeEntries(timesheetFetchParams));
  }, [dispatch, timesheetFetchParams]);

  const handleExportTimesheetCsv = useCallback(async () => {
    try {
      const params = buildMyTimesheetExportQueryParams({
        currentDate,
        timesheetDateRange,
        appliedFilters,
        projectsData,
        clientsData,
        teamsData,
        listRows: timeEntriesData?.list,
        myWorkItems: myWorkItemsList,
      });
      await downloadMyTimesheetExport(params, "csv");
      showSuccess("CSV export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [
    currentDate,
    timesheetDateRange,
    appliedFilters,
    projectsData,
    clientsData,
    teamsData,
    timeEntriesData?.list,
    myWorkItemsList,
  ]);

  const handleExportTimesheetXlsx = useCallback(async () => {
    try {
      const params = buildMyTimesheetExportQueryParams({
        currentDate,
        timesheetDateRange,
        appliedFilters,
        projectsData,
        clientsData,
        teamsData,
        appliedWorkItemId,
        listRows: timeEntriesData?.list,
        myWorkItems: myWorkItemsList,
      });
      await downloadMyTimesheetExport(params, "xlsx");
      showSuccess("Timesheet export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [
    currentDate,
    timesheetDateRange,
    appliedFilters,
    projectsData,
    clientsData,
    teamsData,
    appliedWorkItemId,
    timeEntriesData?.list,
    myWorkItemsList,
  ]);

  useEffect(() => {
    const tabFromUrl = getActiveTabFromUrl();
    const allowedIds = new Set(timeEntryNavItems.map((t) => t.id));
    if (timeEntryNavItems.length > 0 && !allowedIds.has(tabFromUrl)) {
      navigate(`${location.pathname}?tab=${firstAllowedTabId}`, { replace: true });
      return;
    }
    setActiveTab(tabFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, timeEntryNavItems, firstAllowedTabId, location.pathname, navigate]);

  // My timesheet: GET /time-logs/my — date range, optional clientId/teamId/projectId/userId/workItemId, pagination, sort
  useEffect(() => {
    if (activeTab === "timesheet" && canViewTimesheet) {
      dispatch(fetchTimeEntries(timesheetFetchParams));
    }
  }, [dispatch, activeTab, canViewTimesheet, timesheetFetchParams]);

  useEffect(() => {
    if (!teamsData) {
      dispatch(fetchTeamsData({ getAll: true }));
    }
    if (!usersData) {
      dispatch(fetchUsersData({ getAll: true }));
    }
    if (!clientsData) {
      dispatch(fetchClientsData({ getAll: true }));
    }
    if (!projectsData) {
      dispatch(fetchProjectsData({ getAll: true }));
    }
  }, [dispatch, teamsData, usersData, clientsData, projectsData]);

  /** Task filter: GET /work-items/my/all (id + name; same filters as /work-items/my; unpaginated). */
  useEffect(() => {
    if (activeTab === "timesheet" && canViewTimesheet) {
      dispatch(fetchMyWorkItemsAll(myWorkItemsAllQueryParams));
    }
  }, [dispatch, activeTab, canViewTimesheet, myWorkItemsAllQueryParams]);

  // Team summary fetching is handled inside `TeamTimeSummary`

  // Fetch project time summary data when project tab is active
  // Project summary fetching is handled inside `ProjectTimeSummary`

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", tab);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: false,
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterDraft((prev) => ({ ...prev, [name]: value }));
  };

  const applyTimeEntryFilters = () => {
    setAppliedFilters({ ...filterDraft });
    setTimesheetDateRange(timesheetDateRangeDraft);
    setTimeEntryFilterExpanded((prev) => ({ ...prev, timesheet: true }));
    setTimesheetPage(1);
    const wid =
      filterDraft.task?.length === 1
        ? resolveWorkItemIdFromTaskSelection(
            filterDraft.task,
            timeEntriesData?.list,
            myWorkItemsList
          )
        : undefined;
    setAppliedWorkItemId(wid ?? null);
  };

  const handleFilterReset = () => {
    const todayRange = getTodayDateRange();
    setFilterDraft({ ...INITIAL_TIME_FILTERS });
    setAppliedFilters({ ...INITIAL_TIME_FILTERS });
    setTimesheetDateRange(todayRange);
    setTimesheetDateRangeDraft(todayRange);
    setCurrentDate(new Date());
    setAppliedWorkItemId(null);
    setTimesheetPage(1);
  };

  const handleBillingTagChange = (entryId, value) => {
    dispatch(updateBillingTag({ entryId, newTag: value }));
  };

  // Build dropdown options from data (clients / projects / tasks / teams / users)
  const filterOptions = useMemo(() => {
    // For multiselect filters, empty selection means "all",
    // so we don't include synthetic "All ..." options here.
    const clients = new Set();
    const tasks = new Set();
    const teams = new Set();
    const memberNamesFromEntries = new Set();

    if (timeEntriesData?.groups) {
      timeEntriesData.groups.forEach((group) => {
        group.entries.forEach((entry) => {
          if (entry.client) clients.add(entry.client);
          if (entry.task) tasks.add(entry.task);
          const memberName = entry.member || entry.assignee || entry.owner || "";
          if (memberName) memberNamesFromEntries.add(memberName);
          if (entry.team) teams.add(entry.team);
        });
      });
    } else if (timeEntriesData?.list) {
      timeEntriesData.list.forEach((entry) => {
        if (entry.client) clients.add(entry.client);
        if (entry.task) tasks.add(entry.task);
        const memberName = entry.member || entry.assignee || entry.owner || "";
        if (memberName) memberNamesFromEntries.add(memberName);
        if (entry.team) teams.add(entry.team);
      });
    }

    // Teams / clients / projects from Redux (same list shapes as Issues filters)
    getListFromApiResponse(teamsData)
      .map((team) => team.teamName || team.name || "")
      .filter(Boolean)
      .forEach((name) => teams.add(name));

    getListFromApiResponse(clientsData)
      .map((c) => c.clientName ?? c.name ?? "")
      .filter(Boolean)
      .forEach((name) => clients.add(name));

    const projectOptions = mapProjectsToFilterOptions(projectsData);

    if (Array.isArray(myWorkItemsList)) {
      myWorkItemsList.forEach((wi) => {
        const label = workItemToTimesheetTaskLabel(wi);
        if (label) tasks.add(label);
      });
    }

    const userOptionsFromApi = buildUserDropdownOptionsFromUsersData(usersData);
    const seenUserLabels = new Set(userOptionsFromApi.map((o) => o.label));
    const userExtra = [];
    memberNamesFromEntries.forEach((name) => {
      if (name && !seenUserLabels.has(name)) {
        const opt = legacyMemberOption(name);
        if (opt) {
          userExtra.push(opt);
          seenUserLabels.add(name);
        }
      }
    });
    const users = [...userOptionsFromApi, ...userExtra].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );

    // No explicit "All ..." options needed for multiselect; empty selection == all
    return {
      clients: Array.from(clients).sort(),
      projects: projectOptions,
      tasks: Array.from(tasks).sort(),
      teams: Array.from(teams).sort(),
      users,
    };
  }, [timeEntriesData, teamsData, usersData, clientsData, projectsData, myWorkItemsList]);

  /* ===================== DATE NAVIGATION & DUMMY DATA ===================== */
  // Label from YYYY-MM-DD strings (picker/API) — avoid `new Date("YYYY-MM-DD")` (UTC midnight shifts the day).
  const timesheetDateRangeLabel = useMemo(() => {
    const { startDate, endDate } = timesheetDateRange ?? {};
    if (startDate && endDate) {
      if (startDate === endDate) return formatDate(startDate);
      return formatDateRange(startDate, endDate);
    }
    return formatDate(currentDate);
  }, [timesheetDateRange, currentDate]);

  /** Team / project summaries: one calendar day (nav arrows move day-by-day); API uses dateFrom/dateTo for that day */
  const teamProjectDateRangeLabel = useMemo(() => formatDate(currentDate), [currentDate]);

  const summarySelectedDayYmd = useMemo(() => formatDateForInput(currentDate), [currentDate]);

  /** Team / project: cannot navigate past the current week */
  const isSummaryWeekNextDisabled = useMemo(() => {
    const today = new Date();
    const { end: thisWeekEnd } = getWeekRange(today);
    const { end: viewWeekEnd } = getWeekRange(currentDate);
    return viewWeekEnd.getTime() >= thisWeekEnd.getTime();
  }, [currentDate]);

  /** Timesheet: cannot navigate past today */
  const isTimesheetNextDisabled = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cur = new Date(currentDate);
    cur.setHours(0, 0, 0, 0);
    return cur.getTime() >= today.getTime();
  }, [currentDate]);

  const handlePrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    setTimesheetPage(1);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    setTimesheetPage(1);
  };

  // Group API rows by day — filtering is via GET /time-logs/my query params only
  const groups = useMemo(() => {
    if (!timeEntriesData) return [];

    if (timeEntriesData.list) {
      const byDate = {};
      timeEntriesData.list.forEach((entry) => {
        const dateLabel = entry.day || entry.dateLabel || "Timesheet";
        if (!byDate[dateLabel]) {
          byDate[dateLabel] = { dateLabel, entries: [] };
        }
        byDate[dateLabel].entries.push(entry);
      });

      return Object.values(byDate);
    }

    return [];
  }, [timeEntriesData]);

  const summary = useMemo(
    () => resolveTimesheetSummaryFromMetaOrRows(timeEntriesData?.meta, groups),
    [timeEntriesData?.meta, groups]
  );

  const timesheetTotalPages = useMemo(
    () =>
      resolveListTotalPages(
        timeEntriesData?.meta,
        timesheetPageSize,
        timeEntriesData?.list?.length ?? 0
      ),
    [timeEntriesData?.meta, timesheetPageSize, timeEntriesData?.list?.length]
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "all":
        return (
          <RequirePermission permission={PERMISSION_KEYS.ALL_TIME_ENTRY_TIMESHEET_VIEW}>
            <AllTimeEntries />
          </RequirePermission>
        );
      case "timesheet":
        return (
          <RequirePermission permission={PERMISSION_KEYS.TIME_ENTRY_TIMESHEET_VIEW}>
            <TimeSheet
              loading={loading}
              error={error}
              groups={groups}
              filters={filterDraft}
              onFilterChange={handleFilterChange}
              filterOptions={filterOptions}
              summary={summary}
              dateRangeLabel={timesheetDateRangeLabel}
              onPrevWeek={handlePrevDay}
              onNextWeek={handleNextDay}
              disableNext={isTimesheetNextDisabled}
              applyFilters={applyTimeEntryFilters}
              dateRangeDraft={timesheetDateRangeDraft}
              onDateRangeDraftChange={setTimesheetDateRangeDraft}
              handleFilterReset={handleFilterReset}
              filterExpanded={timeEntryFilterExpanded.timesheet}
              onFilterExpandedChange={(open) =>
                setTimeEntryFilterExpanded((prev) => ({ ...prev, timesheet: open }))
              }
              showTeam={false}
              showUser={false}
              showStatus={false}
              showBillingType={false}
              saving={myTimeLogSaving}
              onRefetchTimeEntries={refetchTimesheetEntries}
              onExportCsv={handleExportTimesheetCsv}
              onExportXlsx={handleExportTimesheetXlsx}
              preserveApiRowOrder={true}
              workItemPickerQueryParams={myWorkItemsAllQueryParams}
              onDeleteEntry={async (entry) => {
                if (!entry?.id) return;
                await dispatch(deleteMyTimeLog(entry.id)).unwrap();
                await refetchTimesheetEntries();
              }}
              onEditEntry={async ({ id, minutesLogged, logDate, comment }) => {
                if (!id) return;
                await dispatch(updateMyTimeLog({ id, minutesLogged, logDate, comment })).unwrap();
                await refetchTimesheetEntries();
              }}
              pagination={{
                currentPage: timesheetPage,
                totalPages: timesheetTotalPages,
                pageSize: timesheetPageSize,
                onPageChange: setTimesheetPage,
                onPageSizeChange: (next) => {
                  writeStoredListPageSize(TIME_ENTRY_TIMESHEET_LIST_PAGE_SIZE_STORAGE_KEY, next);
                  setTimesheetPageSize(next);
                  setTimesheetPage(1);
                },
                pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
              }}
            />
          </RequirePermission>
        );
      case "team":
        return (
          <RequirePermission permission={PERMISSION_KEYS.TIME_ENTRY_TEAM_SUMMARY_VIEW}>
            <TeamTimeSummary
              data={teamSummaryData}
              loading={teamSummaryLoading}
              error={teamSummaryError}
              developerData={developerSummaryData}
              developerLoading={developerSummaryLoading}
              developerError={developerSummaryError}
              dateRangeLabel={teamProjectDateRangeLabel}
              onPrevWeek={handlePrevDay}
              onNextWeek={handleNextDay}
              disableNext={isSummaryWeekNextDisabled}
              weekDateFrom={summarySelectedDayYmd}
              weekDateTo={summarySelectedDayYmd}
              filterExpanded={timeEntryFilterExpanded.team}
              onFilterExpandedChange={(open) =>
                setTimeEntryFilterExpanded((prev) => ({ ...prev, team: open }))
              }
              onResetToToday={() => setCurrentDate(new Date())}
            />
          </RequirePermission>
        );
      case "project":
        return (
          <RequirePermission permission={PERMISSION_KEYS.TIME_ENTRY_PROJECT_SUMMARY_VIEW}>
            <ProjectTimeSummary
              data={projectSummaryData}
              loading={projectSummaryLoading}
              error={projectSummaryError}
              dateRangeLabel={teamProjectDateRangeLabel}
              onPrevWeek={handlePrevDay}
              onNextWeek={handleNextDay}
              disableNext={isSummaryWeekNextDisabled}
              weekDateFrom={summarySelectedDayYmd}
              weekDateTo={summarySelectedDayYmd}
              filterExpanded={timeEntryFilterExpanded.project}
              onFilterExpandedChange={(open) =>
                setTimeEntryFilterExpanded((prev) => ({ ...prev, project: open }))
              }
              onResetToToday={() => setCurrentDate(new Date())}
            />
          </RequirePermission>
        );
      default:
        return <TimeSheet title="My Timesheet" icon={RiTimeLine} />;
    }
  };

  const navForUi = timeEntryNavItems.map(({ id, icon, label }) => ({ id, icon, label }));

  return (
    <div>
      <TimeEntryNavigation activeTab={activeTab} onTabChange={handleTabChange} items={navForUi} />
      {renderTabContent()}
    </div>
  );
};

export default TimeEntry;
